/**
 * Homepage content packs: one landing page (src/pages/HomePage.jsx), one pack per country.
 *
 * OPTRA offers the same services in every region, so /sg and /us render the same page: same
 * sections, order, headline, feature steps, pricing cards, comparison, FAQ and CTAs. Everything
 * that is specific to a country lives here and nowhere in the JSX:
 *   - SEO title / keywords and the region used for canonical + JSON-LD URLs
 *   - the hero photo and the three hero statistics
 *   - copy that names the country, its currency, spelling or its funding programs
 *   - where links into the PRODUCT go (`product`), since the US product is a separate app
 *
 * Rules:
 *   - The SG pack holds exactly the strings the Singapore page has always shown. The Singapore
 *     page is guarded byte-for-byte by src/__tests__/HomePage.sg-snapshot.test.jsx.
 *   - The US pack is the same content with the country swapped. Every US-specific FACT (the hero
 *     statistics, named programs) is listed with its source in src/regions/us/CONTENT_REVIEW.md.
 *     Never add or "translate" a number here without adding its source there.
 *   - Company-level content (partners, testimonial, demo video, the "Full Support" card, Cal.com
 *     CTA) is the same everywhere; it is shared below (SUPPORT_CARD) or stays in the page.
 *   - Prices: the Singapore one-time assessment prints no amount (it lives in Stripe). The U.S.
 *     tier prices are printed, as confirmed by the owner (September 2026; US_PRICING below and
 *     src/regions/us/CONTENT_REVIEW.md). Print any other price only once the owner has confirmed it.
 */
import { usAppHref } from "../lib/region";
import { getCountryContent } from "./countryContent";
import { usCheckoutCta } from "../regions/us/checkout";

/**
 * Links into the product.
 * - Singapore: the product is part of this SPA, so targets are region-relative router paths
 *   (`inRouter: true` -> navigate(path); for window.location wrap with currentRegionHref()).
 * - United States: the product is a separate app under /us/app, outside this router
 *   (`inRouter: false` -> plain <a href> / window.location). Its entry points are "/" (the
 *   eligibility assessment) and "/login"; every other product path lands on the app home.
 */
const SG_PRODUCT = {
  inRouter: true,
  // The Singapore landing page is a signed-out surface: arriving on it ends the product session.
  signOutOnLanding: true,
  href: (path = "/dashboard") => path,
};

const US_APP_PATHS = { "/login": "/login" };
const US_PRODUCT = {
  inRouter: false,
  // The US app shares the Clerk session on this host and links back to /us and /us/pricing while
  // signed in (OPTRA US docs/auth.md), so the landing page must not end that session.
  signOutOnLanding: false,
  href: (path = "/") => {
    const clean = String(path || "/").split(/[?#]/)[0];
    return usAppHref(US_APP_PATHS[clean] || "/");
  },
};

const SG_COMPARISON_ROWS = [
  { feature: "Continuous, automated grant discovery across multiple sources", optra: "yes", consultant: "no" },
  { feature: "Instant, rule-based eligibility assessment vs manual review", optra: "yes", consultant: "no" },
  { feature: "Standardised, guided application workflow vs consultant-dependent processes", optra: "yes", consultant: "gray" },
  { feature: "24/7 self-serve access vs business-hours availability", optra: "yes", consultant: "no" },
  { feature: "Ability to manage multiple applications simultaneously without added cost", optra: "yes", consultant: "no" },
  { feature: "Low success fees", optra: "yes", consultant: "no" },
  { feature: "Deep strategic advisory and grant positioning", optra: "yes", consultant: "gray" },
];

// Same rows with U.S. spelling. The success-fee row states the U.S. product's published commitment
// (OPTRA US trust page: "No success fees. No pay-on-award."); Singapore keeps "Low success fees".
const US_COMPARISON_ROWS = SG_COMPARISON_ROWS.map((row) => {
  if (row.feature === "Low success fees") return { ...row, feature: "No success fees" };
  // The U.S. product screens and explains; it does not run an application workflow. These two
  // rows name what it does instead (eligibility memo + readiness checklist; watchlists).
  if (row.feature.startsWith("Standardised, guided application workflow")) {
    return { ...row, feature: "Printable eligibility memo and readiness checklist vs consultant-dependent write-ups" };
  }
  if (row.feature.startsWith("Ability to manage multiple applications")) {
    return { ...row, feature: "Screen and watch multiple notices at once without added cost" };
  }
  return { ...row, feature: row.feature.replace("Standardised", "Standardized") };
});

/** The "Full Support" pricing card is the same everywhere: a Cal.com intro call, no price. */
const SUPPORT_CARD = {
  kicker: "Advisory",
  title: "Full Support",
  tagline: "15-minute intro call for teams considering end-to-end support",
  tags: ["15-minute call", "End-to-end support"],
  body: "We use this call to understand your goals, align on fit, and decide whether a deeper consultation should follow your results if you want support across the wider funding process.",
  bullets: [
    "15-minute introductory call with our team to understand your funding objectives",
    "Guidance on whether a full funding engagement is the right fit for your situation",
    "Next-step planning once eligibility results and likely grant paths are clear",
    "Deeper consultation scheduled later if you decide to proceed with full support",
  ],
};

const SG_HOME = {
  region: "sg",
  seo: {
    title: "OPTRA Labs | AI Grant Platform for SMEs",
    keywords: "SME funding, small business funding, business funding platform, AI funding advisor, eligibility assessment, funding workflow",
  },
  hero: {
    // Kicker above the headline: says who the page is for. The headline itself is shared.
    // Lede under the headline; also the SEO description and the JSON-LD description.
    lede:
      "OPTRA Labs helps SMEs find the government grants and funding programmes they qualify for: it predicts approval likelihood, prioritises the right opportunities, and supports non-dilutive funding from application to award.",
    // Primary hero CTA ("Start Your Assessment"). srHint is read by screen readers only.
    cta: {
      href: "https://buy.stripe.com/14AfZicj3au66e7fJTbsc01",
      newTab: true,
      srHint: "opens secure Stripe checkout in a new tab",
    },
    // The photo is drawn on an aria-hidden canvas and has never carried alt text on Singapore.
    image: {
      src: "/hero-singapore-optra.jpg",
      objectPosition: "center top",
      alt: null,
      ditherPalette: ["#6fa8fa", "#9fc5fb", "#e5efff", "#FFFFFF", "#FFFFFF"],
    },
    stats: [
      { value: "1", animate: false, suffix: "Bn+", label: ["National AI", "investment programmes"] },
      {
        value: 250,
        animate: true,
        suffix: "K+",
        label: ["Average matched", "funding value discovered"],
        // Below the `sm` breakpoint the second line wraps by hand.
        mobileLabel: ["funding value", "discovered"],
      },
      { value: 83, animate: true, suffix: "%", label: ["Singapore businesses", "seek government support"] },
    ],
  },
  trustBar: {
    // Per-partner overrides of src/lib/partnerBranding.js (keyed by partner name). None on Singapore.
    partnerOverrides: {},
  },
  features: {
    subtitle: "Powerful tools designed to help SMEs discover, apply, and win grant funding.",
    eligibilityDescription:
      "Surface high-potential grant opportunities, then evaluate eligibility against programme criteria to identify fit gaps, risk factors, and evidence requirements early, so your team focuses effort where win probability is highest.",
    fitLabel: "Programme fit",
    insightsFooter: "Each run scores 50+ Singapore business funding programmes in our suite",
  },
  pricing: {
    // The self-serve card. The Singapore assessment is paid once through a Stripe payment link;
    // the amount lives in Stripe and is not printed here (see src/regions/us/CONTENT_REVIEW.md).
    assessment: {
      kicker: "Self-serve",
      title: "Eligibility Assessment",
      tagline: "One-time self-serve assessment",
      tags: ["One-time payment", "Immediate access"],
      lead: "Pay once to complete the assessment and receive tailored grant matches with downloadable results.",
      body: "Best for teams that want a clear shortlist of relevant opportunities before deciding whether to engage further.",
      bullets: [
        "Guided assessment covering company profile, project details, and funding needs",
        "Tailored grant matches scored against your inputs and eligibility signals",
        "Downloadable report options for top 5, top 10, or full matched results",
        "Secure Stripe checkout, then direct access to the assessment flow",
      ],
      cta: { href: "https://buy.stripe.com/14AfZicj3au66e7fJTbsc01", newTab: true },
    },
    support: SUPPORT_CARD,
    // schema.org Offer description in the page's JSON-LD.
    offerDescription: "One-time eligibility assessment paid through Stripe checkout; full support priced on request",
  },
  // "View Demo": each site plays its own walkthrough; the lead record says which site and video.
  demo: {
    leadPagePath: "/home",
    video: "/localfiles/Assessment_Demo_2026.mp4",
    description:
      "OPTRA’s Eligibility Assessment helps businesses quickly identify suitable government and non-government grant opportunities based on their company profile, project details, funding needs, and eligibility criteria. Signed in to their OPTRA account, users complete a guided one-time assessment and receive tailored grant recommendations with scores, descriptions, eligibility insights, and downloadable PDF reports covering the top 5, top 10, or full list of matched funding options.",
    note: "This demo contains dummy information only. For how your data is used, see our",
    // Privacy and Terms are pages of this router on Singapore (`inRouter: true` -> <Link>).
    legal: { inRouter: true, privacyHref: "/privacy", termsHref: "/terms" },
  },
  comparison: {
    intro:
      "Both OPTRA Labs and Singapore grant consultants help businesses navigate grants and funding opportunities. The difference lies in how support is delivered through a scalable platform versus traditional engagement models.",
    competitorTitle: "Singapore Grant",
    competitorSubtitle: "Consultants",
    rows: SG_COMPARISON_ROWS,
  },
  product: SG_PRODUCT,
};

/**
 * U.S. prices in USD, confirmed by the owner (September 2026, revised). Every tier starts with the
 * same one-time setup fee, which covers the first month; from the second month each tier has its own
 * monthly price. Upgrading moves to the higher monthly price at once (Stripe charges the prorated
 * difference for the rest of that billing month); there is no second setup fee. Moving to a lower
 * tier is allowed and takes effect at the end of the current billing month. Every tier includes one
 * assessment every two weeks (the next opens 14 days after a run, independent of billing), and an
 * upgrade gives one fresh assessment right away. Mirrored in the FAQ
 * (src/data/faq-data.js), the About page U.S. card, the U.S. blog post and the US app
 * (grant-eligibility src/lib/plan.ts).
 */
const US_PRICING = {
  setupFee: 200,
  monthly: { 1: 39, 2: 79, 3: 129 },
};
const usd = (amount) => `$${amount}`;
const usTierPrice = (tier) => ({
  amount: usd(US_PRICING.monthly[tier]),
  note: "per month",
  recurring: `after a one-time ${usd(US_PRICING.setupFee)} setup fee, first month included`,
});
/** schema.org Offer fields for a tier: the one-time setup fee, plus the tier's monthly price after month one. */
const usTierOffer = (tier) => ({
  price: US_PRICING.setupFee,
  priceSpecification: [
    { "@type": "UnitPriceSpecification", price: US_PRICING.setupFee, description: "One-time setup fee; includes the first month" },
    {
      "@type": "UnitPriceSpecification",
      price: US_PRICING.monthly[tier],
      referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitCode: "MON" },
      description: "Per month after the first month",
    },
  ],
});

const US_HOME = {
  region: "us",
  seo: {
    title: "OPTRA Labs | AI Grant Platform for U.S. Small Businesses",
    keywords:
      "United States small business funding, business funding platform, AI funding advisor, eligibility assessment, funding workflow",
  },
  hero: {
    lede:
      "OPTRA Labs helps U.S. small businesses find the federal and state funding they qualify for: it screens live Grants.gov, SBIR/STTR and state notices against your business profile, backs every verdict with the rule or notice text behind it, and shows what to fix before you apply.",
    // Buying starts at the pricing section (the three tiers), so the button scrolls there.
    // `anchor` is a section id on this page; HomePage builds the region href and scrolls in place.
    cta: { anchor: "pricing", newTab: false, srHint: "choose a plan in the pricing section" },
    // public/hero-us-optra.jpg is a landscape photo (2200x1100); the near tower of the bridge sits
    // about 39% from the left. DitherShader honours objectPosition for "cover" crops, so the frame
    // is centred on the tower.
    image: {
      src: "/hero-us-optra.jpg",
      objectPosition: "38% center",
      alt: "The Golden Gate Bridge in San Francisco, United States",
      ditherPalette: ["#2563EB", "#60A5FA", "#DBEAFE", "#FFFFFF", "#FFFFFF"],
    },
    // Sourced U.S. figures, same three-slot layout. Sources: src/regions/us/CONTENT_REVIEW.md.
    stats: [
      {
        value: "4",
        animate: false,
        suffix: "Bn",
        label: ["Federal SBIR/STTR", "funds invested each year"],
        mobileLabel: ["funds invested", "each year"],
      },
      { value: 323, animate: true, suffix: "K", label: ["SBIR/STTR Phase I", "award ceiling (USD)"] },
      { value: 11, animate: true, suffix: "", label: ["Federal agencies fund", "SBIR/STTR awards"] },
    ],
  },
  trustBar: {
    // Same partners as Singapore. Only the Singapore-worded link of one partner is neutralized.
    partnerOverrides: {
      Osome: { url: "https://osome.com/", ariaLabel: "Visit Osome" },
    },
  },
  features: {
    subtitle: "Powerful tools designed to help U.S. small businesses discover, apply, and win grant funding.",
    eligibilityDescription:
      "Screen every open federal notice against your profile: applicant type, deadlines, the SBA size standard for your NAICS code, SBIR/STTR ownership and research rules, cost share and registrations. Each verdict names the rule or quotes the notice, and every failed check comes with a fix path, so your team focuses effort where you actually qualify.",
    fitLabel: "Program fit",
    // What a run covers (OPTRA US README): the Grants.gov index, DoD SBIR/STTR topics, SAM.gov
    // research notices, state programs and federal tax incentives. No count is claimed.
    insightsFooter: "Each run screens open Grants.gov notices, SBIR/STTR topics, state programs and federal tax incentives",
    // Steps 2 to 5 of the features timeline. Singapore's steps describe its application workflow
    // (roadmap, vendors, compliance review, application package), which the U.S. product does not
    // have; these describe what it does: watchlists, readiness, fix paths, the memo (OPTRA US README).
    steps: [
      {
        title: "Watch Notices and Get Alerted to Changes",
        description:
          "Put any notice on a watchlist. Every night the index refreshes and your screens re-run, and you get a plain-English digest when a new match appears, a verdict changes, a deadline moves or a notice is closing soon.",
      },
      {
        title: "Check Registration Readiness Before the Deadline",
        description:
          "See whether SAM.gov, Grants.gov, the SBA Company Registry, eRA Commons and the other agency systems you need can be ready in time, with each registration's lead time set against the notice's deadline.",
      },
      {
        title: "Follow a Fix Path for Every Failed Check",
        description:
          "Every failed or warned criterion comes with a concrete remediation: what to do, how long it takes, what it costs and who owns it, so a near miss becomes an eligible application.",
      },
      {
        title: "Export an Eligibility Memo and Calendar",
        description:
          "Turn a saved screen into a printable eligibility memo for your team or lender, and add every deadline to your calendar in one click.",
      },
    ],
  },
  pricing: {
    // The U.S. product is paid: one of three tiers, a one-time setup fee (the same on every tier)
    // that covers the first month, then the tier's monthly price (US_PRICING above). Bought through
    // Stripe Payment Links (VITE_US_CHECKOUT_T1..T3, see src/regions/us/checkout.js). No refund
    // promise. Program coverage per src/regions/us/CONTENT_REVIEW.md.
    // Each tier includes the one below it.
    note: `Every tier starts with a one-time ${usd(US_PRICING.setupFee)} setup fee that covers your first month. After that you pay your tier's monthly price (${usd(US_PRICING.monthly[1])}, ${usd(US_PRICING.monthly[2])} or ${usd(US_PRICING.monthly[3])}/month), with one assessment every two weeks. Change tier any time, and cancel any time.`,
    // Under the tier grid.
    upgradeNote: `Changing tier? Move to a higher tier any time from Plan & Billing in the app. You switch to the new monthly price straight away and pay only the prorated difference for the rest of that billing month (nothing extra in your first month), with no second setup fee. The upgrade gives you one fresh assessment right away. You can also move to a lower tier at any time; the change takes effect at the end of your current billing month.`,
    tiers: [
      {
        kicker: "Tier 1",
        title: "Federal",
        tagline: "Eligibility assessment of federal funding, every two weeks",
        price: usTierPrice(1),
        tags: ["Setup fee, then monthly", "Federal"],
        lead: "Screen your business against open federal funding once every two weeks, with the rule or notice text behind every verdict.",
        body: "Best for teams focused on federal grants, SBIR/STTR and federal research funding.",
        bullets: [
          "Grants.gov notices, SBIR/STTR topics, SAM.gov research notices and federal prize competitions",
          "Checked against SBA size standards, applicant types, deadlines and SBIR/STTR rules, with the evidence quoted",
          "Readiness checklist, a fix path for every failed check and a printable eligibility memo",
          "One assessment every two weeks",
        ],
        ctaLabel: "Choose Tier 1",
        get cta() {
          return usCheckoutCta(1);
        },
      },
      {
        kicker: "Tier 2",
        title: "Federal + State",
        tagline: "Eligibility assessment of federal and state funding, every two weeks",
        price: usTierPrice(2),
        tags: ["Setup fee, then monthly", "Federal + state"],
        lead: "Everything in Tier 1, plus the state grants and state programs open to your business.",
        body: "Best for teams that also want state funding in view, from state grant portals to SBIR matching funds.",
        bullets: [
          "Everything in Tier 1",
          "State grant portals and state programs",
          "State SBIR/STTR matching and bridge funds",
          "SSBCI capital programs run through your state",
          "One assessment every two weeks",
        ],
        ctaLabel: "Choose Tier 2",
        get cta() {
          return usCheckoutCta(2);
        },
      },
      {
        kicker: "Tier 3",
        title: "Federal + State + Tax credits",
        tagline: "Eligibility assessment of funding and tax incentives, every two weeks",
        price: usTierPrice(3),
        tags: ["Setup fee, then monthly", "Tax credits"],
        lead: "Everything in Tier 2, plus the federal and state tax credits and incentives your business may qualify for.",
        body: "Best for teams that want the full picture, including incentives claimed on a tax return. Not tax advice: confirm with your CPA.",
        bullets: [
          "Everything in Tier 2",
          "Federal R&D tax credit, including the payroll-tax offset for eligible small businesses",
          "Other federal credits and incentives such as WOTC, each shown with its current status",
          "State tax credits and incentives for your state",
          "One assessment every two weeks",
        ],
        ctaLabel: "Choose Tier 3",
        get cta() {
          return usCheckoutCta(3);
        },
      },
    ],
    // "Full Support" is company-level and stays on offer below the tiers (same Cal.com call).
    support: SUPPORT_CARD,
    // schema.org offers in the page's JSON-LD: one per tier, with the one-time setup fee (USD) and
    // the tier's monthly price after the first month.
    offers: [
      {
        name: "Tier 1: Federal",
        description: `Federal eligibility assessment every two weeks; ${usd(US_PRICING.setupFee)} one-time setup fee, including the first month, then ${usd(US_PRICING.monthly[1])}/month`,
        ...usTierOffer(1),
      },
      {
        name: "Tier 2: Federal + State",
        description: `Federal and state eligibility assessment every two weeks; ${usd(US_PRICING.setupFee)} one-time setup fee, including the first month, then ${usd(US_PRICING.monthly[2])}/month`,
        ...usTierOffer(2),
      },
      {
        name: "Tier 3: Federal + State + Tax credits",
        description: `Federal and state funding and tax incentives: one assessment every two weeks; ${usd(US_PRICING.setupFee)} one-time setup fee, including the first month, then ${usd(US_PRICING.monthly[3])}/month`,
        ...usTierOffer(3),
      },
    ],
    offerDescription: `Eligibility assessment tiers: ${usd(US_PRICING.setupFee)} one-time setup fee, including the first month, then from ${usd(US_PRICING.monthly[1])}/month; full support priced on request`,
  },
  demo: {
    leadPagePath: "/us",
    // "OPTRA US Eligibility Assessment Demo", 76 s, H.264 1800x1080 with AAC audio (re-encoded from the .mov for every browser).
    video: "/localfiles/OPTRA_US_Eligibility_Assessment_Demo.mp4",
    // The video shows the guided assessment on sample data; the U.S. product signs users in
    // instead of asking for an access code, so the code step is not described here.
    description:
      "OPTRA’s U.S. Eligibility Assessment screens open federal and state funding notices against a business profile: company, size and ownership, registrations, grant history, funding and project. The walkthrough shows the guided assessment on sample data and the verdicts it produces, each backed by a named rule or the notice’s own text, with fix paths for anything that falls short. Sign in to run it on your own business.",
    note: "This demo contains dummy information only. For how your data is used, see our",
    // The U.S. legal pages live in the U.S. product (public pages), outside this router.
    legal: { inRouter: false, privacyHref: usAppHref("/privacy"), termsHref: usAppHref("/terms") },
  },
  comparison: {
    intro:
      "Both OPTRA Labs and U.S. grant consultants help businesses navigate grants and funding opportunities. The difference lies in how support is delivered through a scalable platform versus traditional engagement models.",
    competitorTitle: "U.S. Grant",
    competitorSubtitle: "Consultants",
    rows: US_COMPARISON_ROWS,
  },
  product: US_PRODUCT,
};

/**
 * The Singapore site also lets a visitor pick another ASEAN country. Those countries have always
 * shown the Singapore page with three strings adapted; that behaviour is kept here unchanged.
 */
const ASEAN_AUDIENCE = {
  MY: "Malaysian businesses",
  ID: "Indonesian businesses",
  TH: "Thai businesses",
  VN: "Vietnamese businesses",
  PH: "Philippine businesses",
  MM: "Myanmar businesses",
  KH: "Cambodian businesses",
  LA: "Lao businesses",
  BN: "Brunei businesses",
};

function aseanVariant(countryCode) {
  const audience = ASEAN_AUDIENCE[countryCode] || "businesses";
  const countryName = ASEAN_AUDIENCE[countryCode] ? getCountryContent(countryCode).countryName : "Singapore";
  return {
    ...SG_HOME,
    seo: {
      ...SG_HOME.seo,
      keywords: `${countryName} SME funding, business funding platform, AI funding advisor, eligibility assessment, funding workflow`,
    },
    features: {
      ...SG_HOME.features,
      subtitle: `Powerful tools designed to help ${audience} discover, apply, and win grant funding.`,
      insightsFooter: "Multi-scheme fit from your eligibility check",
    },
  };
}

export const HOME_CONTENT = { SG: SG_HOME, US: US_HOME };

/** Homepage content for a country code. Unknown codes get the Singapore page. */
export function getHomeContent(countryCode = "SG") {
  if (HOME_CONTENT[countryCode]) return HOME_CONTENT[countryCode];
  return aseanVariant(countryCode);
}

export default HOME_CONTENT;
